"use client";

import { useCooldownRemaining } from "@/hooks/useCooldownRemaining";
import { useCurrentTime } from "@/hooks/useCurrentTime";
import { Team, Tournament } from "@/types";
import { Icon } from "@iconify/react";
import { useTranslations } from "next-intl";

interface CooldownCounterProps {
  team: Team;
  tournament: Tournament;
  variant?: "inline" | "card" | "compact";
  className?: string;
}

function formatTime(ms: number): string {
  if (ms <= 0) return "00:00:00";

  const hours = Math.floor(ms / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((ms % (1000 * 60)) / 1000);

  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

/**
 * Displays a cooldown counter for a team that has recently completed a challenge.
 * Shows "Cooldown active" with real-time countdown in hh:mm:ss format.
 */
export function CooldownCounter({
  team,
  tournament,
  variant = "inline",
  className = "",
}: CooldownCounterProps) {
  const t = useTranslations();
  const { isOnCooldown, cooldownEndDate } = useCooldownRemaining(
    team,
    tournament,
  );

  // Use shared timer to avoid multiple intervals (fix #5)
  const now = useCurrentTime();

  // Calculate remaining time based on cooldownEndDate
  const timeLeft = cooldownEndDate
    ? Math.max(0, cooldownEndDate.getTime() - now)
    : 0;

  if (!isOnCooldown || timeLeft <= 0) {
    return null;
  }

  const formattedTime = formatTime(timeLeft);

  if (variant === "compact") {
    return (
      <div
        className={`flex items-center gap-1 text-[0.6rem] text-amber-400 ${className}`}
      >
        <span>
          {t("teams.cooldown")} {formattedTime}
        </span>
      </div>
    );
  }

  if (variant === "card") {
    return (
      <div
        className={`flex items-center gap-2.5 rounded-xl border border-amber-500/20 bg-amber-500/5 p-2.5 ${className}`}
      >
        <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-amber-500/10 flex items-center justify-center">
          <Icon icon="solar:hourglass-bold" width={16} className="text-amber-400" />
        </div>
        <span className="text-sm font-medium text-amber-400">
          {t("teams.cooldownActive")} {formattedTime}
        </span>
      </div>
    );
  }

  // Default: inline variant
  return (
    <div
      className={`flex items-center gap-1.5 text-xs text-amber-400 ${className}`}
    >
      <Icon icon="solar:hourglass-bold" width={14} />
      <span>
        {t("teams.cooldownActive")} {formattedTime}
      </span>
    </div>
  );
}
